import React from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Contract } from '../../data/mockContracts';

interface ContractListProps {
  contracts: Contract[];
  onContractClick?: (contract: Contract) => void;
}

export const ContractList: React.FC<ContractListProps> = ({ contracts, onContractClick }) => {
  const getStatusVariant = (status: Contract['status']) => {
    const variants: Record<Contract['status'], 'success' | 'warning' | 'danger' | 'info' | 'default'> = {
      'Borrador': 'default',
      'En Negociación': 'warning',
      'Firmado': 'info',
      'Vigente': 'success',
      'Vencido': 'danger',
    };
    return variants[status];
  };
  
  const getTypeColor = (type: Contract['type']) => {
    const colors: Record<Contract['type'], string> = {
      'Licencia': 'bg-blue-100 text-blue-800',
      'NDA': 'bg-purple-100 text-purple-800',
      'Colaboración': 'bg-green-100 text-green-800',
      'Transferencia': 'bg-orange-100 text-orange-800',
    };
    return colors[type];
  };
  
  const formatCurrency = (value: number, currency: string) => {
    if (value === 0) return 'N/A';
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
    }).format(value);
  };
  
  return (
    <Card padding={false}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                Título
              </th>
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                Tipo
              </th>
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                Estado
              </th>
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                Stakeholder
              </th>
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                Valor
              </th>
              <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">
                Vigencia
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {contracts.map((contract) => (
              <tr
                key={contract.id}
                className="hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => onContractClick?.(contract)}
              >
                <td className="py-4 px-6">
                  <p className="text-sm font-medium text-blue-600 hover:text-blue-800">
                    {contract.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{contract.projectName}</p>
                </td>
                <td className="py-4 px-6">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(contract.type)}`}>
                    {contract.type}
                  </span>
                </td>
                <td className="py-4 px-6">
                  <Badge variant={getStatusVariant(contract.status)}>
                    {contract.status}
                  </Badge>
                </td>
                <td className="py-4 px-6">
                  <p className="text-sm text-gray-900">{contract.stakeholderName}</p>
                </td>
                <td className="py-4 px-6">
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(contract.value, contract.currency)}
                  </p>
                </td>
                <td className="py-4 px-6">
                  <p className="text-sm text-gray-600">
                    {format(new Date(contract.startDate), 'dd/MM/yyyy', { locale: es })}
                  </p>
                  <p className="text-xs text-gray-500">
                    hasta {format(new Date(contract.endDate), 'dd/MM/yyyy', { locale: es })}
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
